import { useEffect, useMemo, useRef, useState } from "react";

interface Course {
  code: string;
  name: string;
}

function normalize(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
}

// Subsequence match: every char of `q` appears in order inside `text`.
// Returns total gap size (lower = better) or -1 if no match.
function subsequenceScore(text: string, q: string): number {
  let ti = 0;
  let lastIdx = -1;
  let gaps = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const ch = q[qi];
    let found = -1;
    for (; ti < text.length; ti++) {
      if (text[ti] === ch) {
        found = ti;
        ti++;
        break;
      }
    }
    if (found === -1) return -1;
    if (lastIdx !== -1) gaps += found - lastIdx - 1;
    lastIdx = found;
  }
  return gaps;
}

function fuzzyScore(name: string, code: string, rawQuery: string): number {
  const q = normalize(rawQuery);
  if (!q) return 0;
  const target = normalize(name);
  const codeTarget = normalize(code.replace(/-/g, " "));

  if (target.includes(q)) return -1000 + target.indexOf(q);
  if (codeTarget.includes(q)) return -500 + codeTarget.indexOf(q);

  const tokens = q.split(" ").filter(Boolean);
  if (tokens.length > 1) {
    const allHit = tokens.every((t) => target.includes(t) || codeTarget.includes(t));
    if (allHit) return -100 + (target.length - q.length);
  }

  const compact = q.replace(/ /g, "");
  const nameSub = subsequenceScore(target, compact);
  if (nameSub !== -1) return nameSub + (target.length - compact.length);
  const codeSub = subsequenceScore(codeTarget, compact);
  if (codeSub !== -1) return 1000 + codeSub;

  return Number.POSITIVE_INFINITY;
}

export function SubjectSearch({
  courses,
  onSelect,
}: {
  courses: Course[];
  onSelect: (course: Course) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return courses;
    return courses
      .map((c) => ({ c, score: fuzzyScore(c.name, c.code, query) }))
      .filter((x) => x.score !== Number.POSITIVE_INFINITY)
      .sort((a, b) => a.score - b.score)
      .map((x) => x.c);
  }, [courses, query]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const showList = open && filtered.length > 0;
  const showEmpty = open && query.trim() && filtered.length === 0;

  return (
    <div ref={containerRef} className="kis-chat-w-full kis-chat-bg-white kis-chat-border kis-chat-border-gray-200 kis-chat-rounded-xl kis-chat-overflow-hidden kis-chat-transition-all focus-within:kis-chat-border-primary/50 focus-within:kis-chat-shadow-[0_0_0_3px_rgba(67,97,238,0.08)]">
      {/* Subject list */}
      {showList && (
        <div className="kis-chat-max-h-36 kis-chat-overflow-y-auto kis-chat-border-b kis-chat-border-gray-100" style={{ scrollbarWidth: "none" }}>
          {filtered.map((c) => (
            <div
              key={c.code}
              onClick={() => {
                onSelect(c);
                setOpen(false);
              }}
              className="kis-chat-px-4 kis-chat-py-1.5 kis-chat-text-[12px] kis-chat-text-gray-600 hover:kis-chat-text-primary hover:kis-chat-bg-gray-50 kis-chat-cursor-pointer"
            >
              {c.name}
            </div>
          ))}
        </div>
      )}

      {showEmpty && (
        <div className="kis-chat-px-4 kis-chat-py-2 kis-chat-border-b kis-chat-border-gray-100">
          <p className="kis-chat-text-[11px] kis-chat-text-gray-400 kis-chat-text-center">No subjects found</p>
        </div>
      )}

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Type to search subjects..."
        className="kis-chat-w-full kis-chat-px-4 kis-chat-py-3 kis-chat-bg-transparent kis-chat-text-[13px] kis-chat-text-gray-700 kis-chat-placeholder-gray-400 kis-chat-outline-none kis-chat-border-none"
      />
    </div>
  );
}
